import { InfoIcon, PlusIcon } from '@phosphor-icons/react';
import { Formik } from 'components/form/formik';
import diff from 'object-diff';
import { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import * as Yup from 'yup';

import { Alert } from '../../../../components/alert/Alert';
import { Button } from '../../../../components/button/Button';
import { CheckboxField } from '../../../../components/checkbox/CheckboxField';
import { InputField } from '../../../../components/input/InputField';
import { TextAreaField } from '../../../../components/textarea/TextAreaField';
import { LoadingMethod, OrderStatus, TrailerType } from 'src/types/enums';
import { getHistoricalOrderLinesForSuggestions, updateOrder, type HistoricalOrderLinesResponse } from '../../models';
import type { BusinessUnitValue } from '../../endpoints/shared.types';
import { getDisplayError } from '../../../../utils/get-display-error';
import { CustomerComboboxField } from '../../../customer/CustomerComboboxField';
import { SupplierComboboxField } from '../../../supplier/SupplierComboboxField';
import { useOrder } from './orderContext';
import { TrailerTypes } from './TrailerTypes';
import { LoadingMethods } from './LoadingMethods';
import { TrailerComboboxField } from '../../../trailer/TrailerComboboxField';
import { decimateAndFormatNumber, parseNumberInput } from '../../../../utils/number';
import { calculateLinesTotalExclVat } from '../../utils/price';
import { Card } from 'components/Card';
import { captureException } from '@sentry/react';
import { trackEvent } from '../../../../utils/track-event';
import { BUSINESS_UNIT_OPTIONS } from '../../constants';
import { SimpleSelectField } from 'components/select/SimpleSelectField';
import { useTranslation } from 'contexts/translation-context';
import { useAuth } from '../../../../contexts/auth-context';
import { roleToNumber } from '../../../auth/roles.client';

const updateOrderSchema = Yup.object().shape({
  customer: Yup.mixed().nullable().required('Vereist'),
  customerRef: Yup.string(),
  internalNotes: Yup.string(),
  externalNotes: Yup.string(),
  requiresAnonymousCMR: Yup.boolean().required('Vereist'),
  loadingMethod: Yup.mixed().nullable().required('errors.required'),
});

function getLineMatchingKey(stops: Array<{ location: { country: string; postalCode: string } }>): string {
  return stops
    .map((stop) => (stop.location.country + stop.location.postalCode).toUpperCase())
    .sort()
    .join(',');
}

export const UpdateOrder = () => {
  const { i18n } = useTranslation();
  const { me } = useAuth();
  const { order, refreshData } = useOrder();
  const isPurchaseReadOnly = order.purchaseLocked && roleToNumber(me.role) < roleToNumber('InternalAdmin');
  const [historicalOrderLines, setHistoricalOrderLines] = useState<HistoricalOrderLinesResponse['lines']>([]);

  useEffect(() => {
    if (order && !order.supplier) {
      const run = async () => {
        try {
          const result = await getHistoricalOrderLinesForSuggestions(order.id);
          setHistoricalOrderLines(result.lines);
        } catch (err) {
          console.error('Failed to fetch supplier suggestions', err);
        }
      };
      run().catch(console.error);
    }
  }, [order]);

  const initialValues = useMemo(() => {
    return {
      supplier: order.supplier ?? null,
      customer: order.customer,
      businessUnit: BUSINESS_UNIT_OPTIONS.find((v) => v.key === order.businessUnit),
      noPurchase: order.noPurchase,
      customerRef: order.customerRef,
      internalNotes: order.internalNotes,
      externalNotes: order.externalNotes,
      manualFullKm: order.manualFullKm ?? '',
      manualEmptyKm: order.manualEmptyKm ?? '',
      requiresAnonymousCMR: order.requiresAnonymousCMR,
      trailer: order.trailer ?? null,
      allowedTrailerTypes: order.allowedTrailerTypes,
      loadingMethod: order.loadingMethod ?? null,
    };
  }, [order]);

  const suggestedSuppliers = useMemo(() => {
    const suppliers = new Map<number, any>();
    const supplierOrderLines = new Map<number, typeof historicalOrderLines>();

    const lineKeys = order.lines.map((line) => getLineMatchingKey(line.stops));

    for (const orderLine of historicalOrderLines) {
      const supplierId = orderLine.order.supplier?.id;
      if (supplierId) {
        const existingOrderLines = supplierOrderLines.get(supplierId) ?? [];
        suppliers.set(supplierId, orderLine.order.supplier);
        existingOrderLines.push(orderLine);
        supplierOrderLines.set(supplierId, existingOrderLines);
      }
    }

    const avgPerSupplier = new Map<number, number>();
    for (const [supplierId, supplierOrders] of supplierOrderLines) {
      const pricesPerKey = new Map<string, number[]>();

      for (const orderLine of supplierOrders) {
        const lineKey = getLineMatchingKey(orderLine.stops);
        const price = calculateLinesTotalExclVat(orderLine.purchases);
        const existingPrices = pricesPerKey.get(lineKey) ?? [];
        existingPrices.push(price);
        pricesPerKey.set(lineKey, existingPrices);
      }

      let hasAllStops = true;
      const averagePricePerKey = [];
      for (const lineKey of lineKeys) {
        const prices = pricesPerKey.get(lineKey);
        if (!prices?.length) {
          hasAllStops = false;
          continue;
        }
        averagePricePerKey.push(prices.reduce((a, b) => a + b, 0) / prices.length);
      }

      if (!hasAllStops) {
        continue;
      }

      avgPerSupplier.set(
        supplierId,
        averagePricePerKey.reduce((a, b) => a + b, 0),
      );
    }

    let suppliersResultValue = Array.from(suppliers.values());
    suppliersResultValue = suppliersResultValue.sort((a, b) => {
      return (
        (avgPerSupplier.get(a.id) ?? Number.MAX_SAFE_INTEGER) - (avgPerSupplier.get(b.id) ?? Number.MAX_SAFE_INTEGER)
      );
    });

    return {
      suppliers: suppliersResultValue,
      avgPerSupplier,
    };
  }, [historicalOrderLines]);

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={updateOrderSchema}
      onSubmit={async (values) => {
        try {
          const patch: Partial<typeof values> = diff(initialValues, values);
          const { customer, supplier, businessUnit, trailer, manualFullKm, manualEmptyKm, ...otherValues } = patch;
          const customerId = customer?.id;
          const supplierId = supplier === undefined ? undefined : (supplier?.id ?? null);
          const trailerId = trailer === undefined ? undefined : (trailer?.id ?? null);
          const parseKm = (value: unknown): number | null =>
            value === '' || value == null ? null : parseNumberInput(String(value), 0);
          await updateOrder(order.id, {
            customerId,
            supplierId,
            trailerId,
            businessUnit: businessUnit?.key as BusinessUnitValue,
            ...('manualFullKm' in patch ? { manualFullKm: parseKm(manualFullKm) } : {}),
            ...('manualEmptyKm' in patch ? { manualEmptyKm: parseKm(manualEmptyKm) } : {}),
            ...otherValues,
          });
          trackEvent('order_updated', {
            order_id: order.id,
            fields_changed: Object.keys(patch),
          });
          toast.success('Order aangepast');
          await refreshData();
        } catch (err: any) {
          captureException(err);
          toast.error('Kon order niet aanpassen: ' + getDisplayError(err));
        }
      }}
    >
      {({ handleSubmit, isSubmitting, values, setFieldValue, setFieldTouched, errors, touched, submitCount }) => {
        const showLoadingMethodError = Boolean((touched.loadingMethod || submitCount > 0) && errors.loadingMethod);

        return (
          <form onSubmit={handleSubmit} className="grid gap-4">
            <Card title="Algemene informatie">
              <div></div>
              {!order.customer && order.originalCustomerName && (
                <div className="text-sm">{`Gedetecteerde klant niet gevonden: ${order.originalCustomerName}`}</div>
              )}
              <CustomerComboboxField name="customer" isDisabled={isSubmitting} />

              {isPurchaseReadOnly && (
                <Alert type="info" icon={<InfoIcon className="w-5 h-5" />} title="Aankoopzijde geblokkeerd">
                  Het ritoverzicht of de self billing van deze order is al verzonden, dus de aankoopzijde kan niet meer
                  worden aangepast.
                </Alert>
              )}

              <SupplierComboboxField
                name="supplier"
                isDisabled={isSubmitting || isPurchaseReadOnly}
                isNullable={order.status === OrderStatus.Draft || order.status === OrderStatus.Created}
              />

              {suggestedSuppliers.suppliers.length > 0 && !order.supplier && !isPurchaseReadOnly && (
                <div className="flex flex-wrap gap-2 mb-4">
                  <div className="text-fg-muted">Voorgestelde vervoerders:</div>
                  {suggestedSuppliers.suppliers.map((suggestedSupplier) => {
                    const avgPrice = suggestedSuppliers.avgPerSupplier.get(suggestedSupplier.id);
                    let txtValue = suggestedSupplier.name;

                    if (avgPrice) {
                      txtValue += ` (€ ${decimateAndFormatNumber(avgPrice, 2, { decimalSeperator: ',', removeTrailingZeros: true })})`;
                    }

                    return (
                      <div
                        key={`suggested-supplier-${suggestedSupplier.id}`}
                        className="cursor-pointer hover:text-brand-text text-fg whitespace-nowrap"
                        onClick={() => {
                          setFieldValue('supplier', suggestedSupplier);
                        }}
                      >
                        {txtValue}
                      </div>
                    );
                  })}
                </div>
              )}

              <TrailerComboboxField name="trailer" isDisabled={isSubmitting} />

              <div className="lg:grid grid-cols-2 gap-4">
                <InputField
                  type="text"
                  labelText="Factuurreferentie klant"
                  name="customerRef"
                  isDisabled={isSubmitting}
                />

                <SimpleSelectField
                  name="businessUnit"
                  labelText="Business unit"
                  isDisabled={isSubmitting}
                  items={BUSINESS_UNIT_OPTIONS}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <CheckboxField labelText="Moet anonieme CMR aanmaken" name="requiresAnonymousCMR" />
                <CheckboxField
                  labelText="Geen aankopen"
                  name="noPurchase"
                  isDisabled={isSubmitting || isPurchaseReadOnly}
                />
              </div>
            </Card>

            <Card title="Notities">
              <div className="lg:grid grid-cols-2 gap-4">
                <TextAreaField
                  labelText="Interne notities"
                  name="internalNotes"
                  isDisabled={isSubmitting}
                  spellCheck={true}
                />
                <TextAreaField
                  labelText="Externe notities (voor vervoerder)"
                  name="externalNotes"
                  isDisabled={isSubmitting}
                  spellCheck={true}
                />
              </div>
            </Card>

            <Card title="Gereden kilometers">
              <div className="lg:grid grid-cols-2 gap-4">
                <InputField type="number" step="1" labelText="Volle km" name="manualFullKm" isDisabled={isSubmitting} />
                <InputField type="number" step="1" labelText="Lege km" name="manualEmptyKm" isDisabled={isSubmitting} />
              </div>
              {(values.manualFullKm !== '' || values.manualEmptyKm !== '') && (
                <div className="text-sm text-fg-muted mt-2">
                  {`Totaal gereden: ${(Number(values.manualFullKm) || 0) + (Number(values.manualEmptyKm) || 0)} km`}
                </div>
              )}
              {(values.manualFullKm === '' || values.manualEmptyKm === '') && (
                <div className="text-sm text-warning-text mt-2">
                  Gelieve zowel de volle als de lege kilometers in te vullen voor een correct truckrapport.
                </div>
              )}
            </Card>

            <Card title="Trailer types">
              <TrailerTypes
                value={values.allowedTrailerTypes as TrailerType[]}
                onChange={(newTrailerTypes) => {
                  setFieldValue('allowedTrailerTypes', newTrailerTypes);
                }}
                isMultiSelect={true}
              />
            </Card>

            <Card title="Wijze van belading">
              <LoadingMethods
                value={values.loadingMethod as LoadingMethod | null}
                onChange={(newLoadingMethod) => {
                  setFieldValue('loadingMethod', newLoadingMethod);
                  setFieldTouched('loadingMethod', true, false);
                }}
              />
              {showLoadingMethodError && (
                <div className="text-sm text-negative-text mt-2">{i18n('errors.required')}</div>
              )}
            </Card>

            <div>
              <Button
                type="submit"
                color="primary"
                isDisabled={isSubmitting}
                isLoading={isSubmitting}
                iconLeft={<PlusIcon className="button-icon" />}
              >
                Bewaar aanpassingen
              </Button>
            </div>
          </form>
        );
      }}
    </Formik>
  );
};
